/**
 * AccountingPage — Admin financial dashboard.
 *
 * Read-model over `student_payments` + `payment_refunds`. Everything in
 * ₱PHP (single-currency). Shows headline revenue, a daily trend, per-
 * course / per-method / per-admin breakdowns, and a searchable list
 * with CSV export. Also lets admins record manual refunds.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Calculator, Download, Search, RotateCcw, Undo2, TrendingUp, Wallet, Calendar,
  Loader2, Trash2, PlusCircle,
} from "lucide-react";
import { toast } from "sonner";
import PageIntroCard from "@/components/PageIntroCard";

const PHP = new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP", minimumFractionDigits: 0 });
const NUM = new Intl.NumberFormat("en-PH");

const PIE_COLORS = ["#4F46E5", "#0EA5E9", "#F97316", "#EC4899", "#10B981", "#F59E0B", "#8B5CF6", "#14B8A6"];

const PERIODS = [
  { v: "today", label: "Today" },
  { v: "7d", label: "Last 7 days" },
  { v: "30d", label: "Last 30 days" },
  { v: "mtd", label: "This month" },
  { v: "ytd", label: "This year" },
  { v: "custom", label: "Custom range" },
];

function periodBounds(v) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (v === "today") return { from: iso(now), to: iso(now) };
  if (v === "7d") { const d = new Date(now); d.setDate(d.getDate() - 7); return { from: iso(d), to: iso(now) }; }
  if (v === "30d") { const d = new Date(now); d.setDate(d.getDate() - 30); return { from: iso(d), to: iso(now) }; }
  if (v === "mtd") return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
  if (v === "ytd") return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
  return { from: "", to: "" };
}

export default function AccountingPage() {
  const { user } = useAuth();
  const canApprove = hasPerm(user, "enrollment_request", "approve");
  const isSuper = user?.role === "super_admin";

  const [period, setPeriod] = useState("30d");
  const [range, setRange] = useState(() => periodBounds("30d"));
  const [summary, setSummary] = useState(null);
  const [loadingSum, setLoadingSum] = useState(false);

  const [payments, setPayments] = useState([]);
  const [loadingPay, setLoadingPay] = useState(false);
  const [search, setSearch] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [courseFilter, setCourseFilter] = useState("all");

  const [refunds, setRefunds] = useState([]);
  const [refundDialog, setRefundDialog] = useState({ open: false });
  const [refundForm, setRefundForm] = useState({ payment_id: "", amount: "", reason: "", reference: "" });

  // Load summary when the date-range changes.
  const loadSummary = useCallback(async () => {
    setLoadingSum(true);
    try {
      const r = await api.get("/admin/accounting/summary", {
        params: { date_from: range.from || undefined, date_to: range.to || undefined },
      });
      setSummary(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load summary");
    } finally { setLoadingSum(false); }
  }, [range]);

  const loadPayments = useCallback(async () => {
    setLoadingPay(true);
    try {
      const params = {
        date_from: range.from || undefined,
        date_to: range.to || undefined,
        q: search || undefined,
        method: methodFilter !== "all" ? methodFilter : undefined,
        course_id: courseFilter !== "all" ? courseFilter : undefined,
      };
      const r = await api.get("/admin/accounting/payments", { params });
      setPayments(r.data?.payments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payments");
    } finally { setLoadingPay(false); }
  }, [range, search, methodFilter, courseFilter]);

  const loadRefunds = useCallback(async () => {
    try {
      const r = await api.get("/admin/accounting/refunds", {
        params: { date_from: range.from || undefined, date_to: range.to || undefined },
      });
      setRefunds(r.data?.refunds || []);
    } catch { /* silent */ }
  }, [range]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadPayments(); }, [loadPayments]);
  useEffect(() => { loadRefunds(); }, [loadRefunds]);

  const setPeriodPreset = (v) => {
    setPeriod(v);
    if (v !== "custom") setRange(periodBounds(v));
  };

  const handleExport = () => {
    // Build the URL directly and rely on the browser to auth via the pm_token
    // in cookie / header. We attach the token manually so the CSV download
    // works even when the browser doesn't send our Authorization header.
    const params = new URLSearchParams();
    if (range.from) params.set("date_from", range.from);
    if (range.to) params.set("date_to", range.to);
    if (search) params.set("q", search);
    if (methodFilter !== "all") params.set("method", methodFilter);
    if (courseFilter !== "all") params.set("course_id", courseFilter);

    const token = localStorage.getItem("pm_token");
    const url = `${API}/admin/accounting/export?${params.toString()}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error(r.statusText);
        return r.blob();
      })
      .then((blob) => {
        const dl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = dl;
        a.download = `prepmasters-payments-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(dl);
      })
      .catch(() => toast.error("Export failed"));
  };

  const submitRefund = async () => {
    if (!refundForm.amount || !refundForm.reason) {
      toast.error("Amount and reason are required");
      return;
    }
    try {
      await api.post("/admin/accounting/refunds", {
        payment_id: refundForm.payment_id || null,
        amount: parseFloat(String(refundForm.amount).replace(/,/g, "")),
        reason: refundForm.reason,
        reference: refundForm.reference || null,
      });
      toast.success("Refund recorded");
      setRefundDialog({ open: false });
      setRefundForm({ payment_id: "", amount: "", reason: "", reference: "" });
      loadRefunds();
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record refund");
    }
  };

  const deleteRefund = async (id) => {
    if (!window.confirm("Delete this refund entry? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/accounting/refunds/${id}`);
      toast.success("Refund deleted");
      loadRefunds();
      loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const uniqueMethods = useMemo(() => {
    const set = new Set();
    (summary?.by_method || []).forEach((m) => set.add(m.method));
    return Array.from(set);
  }, [summary]);

  const uniqueCourses = useMemo(() => {
    const list = summary?.by_course || [];
    return list.map((c) => ({ id: c.course_id, name: c.course_name }));
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="admin-accounting">
      <PageIntroCard
        icon={Calculator}
        title="Accounting"
        body="Track revenue, receipts, refunds, and the admins who verified each payment. Every amount is in ₱PHP. Export any filtered view to CSV for your bookkeeper or BIR filing."
      />

      {/* Headline cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Today", key: "today", tone: "from-indigo-500 to-violet-600" },
          { label: "This month", key: "mtd", tone: "from-emerald-500 to-teal-600" },
          { label: "This year", key: "ytd", tone: "from-amber-500 to-orange-600" },
          { label: "All-time", key: "all_time", tone: "from-slate-700 to-slate-900" },
        ].map((c) => (
          <Card key={c.key} className={`p-4 text-white bg-gradient-to-br ${c.tone} border-none`} data-testid={`headline-${c.key}`}>
            <div className="text-xs uppercase tracking-wider opacity-80">{c.label}</div>
            <div className="text-2xl font-bold tabular-nums mt-1">
              {loadingSum ? "…" : PHP.format(summary?.headline?.[c.key] || 0)}
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriodPreset}>
              <SelectTrigger className="h-9 w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={range.from} onChange={(e) => { setPeriod("custom"); setRange({ ...range, from: e.target.value }); }} className="h-9 w-40" data-testid="date-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={range.to} onChange={(e) => { setPeriod("custom"); setRange({ ...range, to: e.target.value }); }} className="h-9 w-40" data-testid="date-to" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { loadSummary(); loadPayments(); loadRefunds(); }} data-testid="refresh-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={handleExport} className="bg-indigo-600 hover:bg-indigo-700" data-testid="export-csv-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Window totals strip */}
      {summary?.window_totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 border-slate-200" data-testid="window-gross">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Gross Revenue</div>
            <div className="text-lg font-bold tabular-nums text-emerald-700">{PHP.format(summary.window_totals.gross)}</div>
          </Card>
          <Card className="p-3 border-slate-200" data-testid="window-refunds">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Undo2 className="w-3 h-3" /> Refunds</div>
            <div className="text-lg font-bold tabular-nums text-rose-700">− {PHP.format(summary.window_totals.refunds)}</div>
          </Card>
          <Card className="p-3 border-slate-200" data-testid="window-net">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Net Revenue</div>
            <div className="text-lg font-bold tabular-nums text-slate-900">{PHP.format(summary.window_totals.net)}</div>
          </Card>
          <Card className="p-3 border-slate-200" data-testid="window-count">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Payments</div>
            <div className="text-lg font-bold tabular-nums text-slate-900">{NUM.format(summary.window_totals.count)}</div>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-4 border-slate-200 lg:col-span-2" data-testid="revenue-trend">
          <div className="font-display font-bold text-sm mb-2">Revenue Trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={summary?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => `₱${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                <Tooltip formatter={(v) => PHP.format(v)} labelFormatter={(l) => `Date: ${l}`} />
                <Line type="monotone" dataKey="amount" stroke="#4F46E5" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4 border-slate-200" data-testid="method-pie">
          <div className="font-display font-bold text-sm mb-2">Revenue by Method</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={summary?.by_method || []} dataKey="amount" nameKey="method" innerRadius={40} outerRadius={80} paddingAngle={2}>
                  {(summary?.by_method || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => PHP.format(v)} />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Breakdown tables */}
      <div className="grid lg:grid-cols-2 gap-4">
        <BreakdownTable
          title="Revenue by Course"
          testid="by-course"
          rows={summary?.by_course || []}
          columns={[
            { key: "course_name", label: "Course" },
            { key: "count", label: "Payments", align: "right", format: NUM.format },
            { key: "amount", label: "Amount", align: "right", format: PHP.format, bold: true },
          ]}
        />
        <BreakdownTable
          title="Verified By"
          testid="by-admin"
          empty="Only manual (non-IAP) payments show an admin here."
          rows={summary?.by_admin || []}
          columns={[
            { key: "admin_name", label: "Admin" },
            { key: "count", label: "Verifications", align: "right", format: NUM.format },
            { key: "amount", label: "Amount", align: "right", format: PHP.format, bold: true },
          ]}
        />
      </div>

      {/* Payments + Refunds tabs */}
      <Tabs defaultValue="payments">
        <TabsList>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-refunds">Refunds ({refunds.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-4 space-y-3">
          <Card className="p-3 border-slate-200 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Search (name / email / reference)</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. juan@…" className="h-9 pl-8" data-testid="search-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger className="h-9 w-48" data-testid="method-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All methods</SelectItem>
                  {uniqueMethods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Course</Label>
              <Select value={courseFilter} onValueChange={setCourseFilter}>
                <SelectTrigger className="h-9 w-56" data-testid="course-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All courses</SelectItem>
                  {uniqueCourses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          <Card className="p-0 border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-2.5">Date</th>
                    <th className="text-left p-2.5">Student</th>
                    <th className="text-left p-2.5">Course</th>
                    <th className="text-left p-2.5">Method</th>
                    <th className="text-left p-2.5">Reference</th>
                    <th className="text-right p-2.5">Amount</th>
                    <th className="text-left p-2.5">Status</th>
                    <th className="text-left p-2.5">Verified By</th>
                    <th className="text-right p-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {loadingPay ? (
                    <tr><td colSpan={9} className="p-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : payments.length === 0 ? (
                    <tr><td colSpan={9} className="p-6 text-center text-slate-500">No payments found for these filters.</td></tr>
                  ) : payments.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                      <td className="p-2.5 text-xs text-slate-600 whitespace-nowrap">{(p.confirmed_at || p.verified_at || p.submitted_at || "").slice(0, 10)}</td>
                      <td className="p-2.5">
                        <div className="font-medium text-slate-900">{p.student_name || "—"}</div>
                        <div className="text-[11px] text-slate-500">{p.student_email}</div>
                      </td>
                      <td className="p-2.5">{p.course_name}</td>
                      <td className="p-2.5"><Badge variant="outline" className="font-normal">{p.payment_system_name || "—"}</Badge></td>
                      <td className="p-2.5 font-mono text-xs">{(p.payment_reference_number || p.apple_transaction_id || p.google_purchase_token || "—").toString().slice(0, 24)}</td>
                      <td className="p-2.5 text-right font-mono tabular-nums font-semibold">{p.amount_paid != null ? PHP.format(p.amount_paid) : <span className="text-slate-400">—</span>}</td>
                      <td className="p-2.5"><StatusBadge status={p.payment_status} /></td>
                      <td className="p-2.5 text-xs text-slate-600">{p.confirmed_by_admin_name || p.payment_verified_by_name || "—"}</td>
                      <td className="p-2.5 text-right">
                        {canApprove && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setRefundForm({ payment_id: p.id, amount: String(p.amount_paid || ""), reason: "", reference: "" }); setRefundDialog({ open: true }); }} data-testid={`refund-btn-${p.id}`}>
                            <Undo2 className="w-3 h-3 mr-1" /> Refund
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="refunds" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Total refunds in this range: <span className="font-mono font-semibold text-rose-700">{PHP.format(refunds.reduce((s, r) => s + (r.amount || 0), 0))}</span>
            </div>
            {canApprove && (
              <Button size="sm" onClick={() => { setRefundForm({ payment_id: "", amount: "", reason: "", reference: "" }); setRefundDialog({ open: true }); }} data-testid="new-refund-btn">
                <PlusCircle className="w-3.5 h-3.5 mr-1" /> Record Refund
              </Button>
            )}
          </div>

          <Card className="p-0 border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="refunds-table">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-2.5">Date</th>
                    <th className="text-left p-2.5">Student</th>
                    <th className="text-left p-2.5">Course</th>
                    <th className="text-right p-2.5">Amount</th>
                    <th className="text-left p-2.5">Reason</th>
                    <th className="text-left p-2.5">Reference</th>
                    <th className="text-left p-2.5">Recorded By</th>
                    <th className="text-right p-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.length === 0 ? (
                    <tr><td colSpan={8} className="p-6 text-center text-slate-500">No refunds recorded in this range.</td></tr>
                  ) : refunds.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`refund-row-${r.id}`}>
                      <td className="p-2.5 text-xs text-slate-600">{(r.refunded_at || "").slice(0, 10)}</td>
                      <td className="p-2.5">
                        <div className="font-medium text-slate-900">{r.student_name || "—"}</div>
                        <div className="text-[11px] text-slate-500">{r.student_email}</div>
                      </td>
                      <td className="p-2.5">{r.course_name || "—"}</td>
                      <td className="p-2.5 text-right font-mono tabular-nums font-semibold text-rose-700">− {PHP.format(r.amount)}</td>
                      <td className="p-2.5 text-xs">{r.reason}</td>
                      <td className="p-2.5 font-mono text-xs">{r.reference || "—"}</td>
                      <td className="p-2.5 text-xs text-slate-600">{r.refunded_by_admin_name || "—"}</td>
                      <td className="p-2.5 text-right">
                        {isSuper && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600 hover:text-rose-800" onClick={() => deleteRefund(r.id)} data-testid={`delete-refund-${r.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Refund dialog */}
      <Dialog open={refundDialog.open} onOpenChange={(o) => setRefundDialog({ open: o })}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Undo2 className="w-4 h-4 text-rose-600" /> Record Refund</DialogTitle>
            <DialogDescription>Enter the refund amount in ₱PHP and the reason. Links to the original payment when provided.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {refundForm.payment_id && (
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
                Linked to payment <span className="font-mono">{refundForm.payment_id.slice(0, 8)}…</span>
              </div>
            )}
            <div>
              <Label className="text-xs">Amount (₱PHP)</Label>
              <Input type="number" step="0.01" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} placeholder="e.g. 499" data-testid="refund-amount" />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} placeholder="Explain why this refund was issued." rows={3} data-testid="refund-reason" />
            </div>
            <div>
              <Label className="text-xs">Reference no. (optional)</Label>
              <Input value={refundForm.reference} onChange={(e) => setRefundForm({ ...refundForm, reference: e.target.value })} placeholder="Bank transaction / GCash ref" data-testid="refund-reference" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialog({ open: false })}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={submitRefund} data-testid="submit-refund-btn">Record refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function BreakdownTable({ title, rows, columns, testid, empty }) {
  return (
    <Card className="p-4 border-slate-200" data-testid={testid}>
      <div className="font-display font-bold text-sm mb-2">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`p-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="p-4 text-center text-slate-500 text-xs">{empty || "No data in this range."}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                {columns.map((c) => (
                  <td key={c.key} className={`p-2 ${c.align === "right" ? "text-right font-mono tabular-nums" : ""} ${c.bold ? "font-semibold" : ""}`}>
                    {c.format ? c.format(r[c.key]) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    verified: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "Verified" },
    confirmed: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Confirmed" },
    pending_verification: { color: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
    rejected: { color: "bg-rose-100 text-rose-800 border-rose-200", label: "Rejected" },
  };
  const m = map[status] || { color: "bg-slate-100 text-slate-700 border-slate-200", label: status || "—" };
  return <Badge variant="outline" className={`${m.color} font-normal text-[11px]`}>{m.label}</Badge>;
}
